import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GetWithAuth, SERVER_URL } from '../utils/api';
import ReceiptUpload from '../components/ReceiptUpload';

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const { token, loading: authLoading } = useAuth();

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const data = await GetWithAuth(`/orders/${id}`);
      if (data.success) {
        setOrder(data.order);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Esperar a que termine de cargar la autenticación
    if (authLoading) {
      console.log('⏳ OrderDetail - Esperando autenticación...');
      return;
    }

    if (token) {
      console.log('✅ OrderDetail - Usuario autenticado, cargando orden...');
      fetchOrder();
    } else {
      console.log('❌ OrderDetail - Usuario no autenticado');
      setLoading(false);
    }
  }, [id, authLoading, token, fetchOrder]);

  const handleReceiptUploadSuccess = (receiptData) => {
    // Refresh order data after successful upload
    fetchOrder();
    setShowReceiptUpload(false);
  };

  const getStatusBadge = (status, type = 'order') => {
    let config;

    if (type === 'receipt') {
      const receiptStatusConfig = {
        'pendiente': { label: 'Pendiente', color: '#f59e0b', bg: '#fef3c7' },
        'aprobado': { label: 'Aprobado', color: '#059669', bg: '#d1fae5' },
        'en_revision': { label: 'En Revisión', color: '#2563eb', bg: '#dbeafe' },
        'rechazado': { label: 'Rechazado', color: '#dc2626', bg: '#fee2e2' }
      };
      // Normalizar el status a minúsculas
      const normalizedStatus = (status || '').toLowerCase();
      config = receiptStatusConfig[normalizedStatus] || receiptStatusConfig['pendiente'];
    } else {
      // Order status config - incluir estados en español e inglés
      const orderStatusConfig = {
        'pending': { label: 'Pendiente', color: '#f59e0b', bg: '#fef3c7' },
        'pendiente': { label: 'Pendiente', color: '#f59e0b', bg: '#fef3c7' },
        'confirmed': { label: 'Confirmada', color: '#2563eb', bg: '#dbeafe' },
        'confirmado': { label: 'Confirmada', color: '#2563eb', bg: '#dbeafe' },
        'processing': { label: 'Procesando', color: '#7c3aed', bg: '#ede9fe' },
        'procesando': { label: 'Procesando', color: '#7c3aed', bg: '#ede9fe' },
        'shipped': { label: 'Enviado', color: '#059669', bg: '#d1fae5' },
        'enviado': { label: 'Enviado', color: '#059669', bg: '#d1fae5' },
        'delivered': { label: 'Entregado', color: '#047857', bg: '#a7f3d0' },
        'entregado': { label: 'Entregado', color: '#047857', bg: '#a7f3d0' },
        'cancelled': { label: 'Cancelado', color: '#dc2626', bg: '#fee2e2' },
        'cancelado': { label: 'Cancelado', color: '#dc2626', bg: '#fee2e2' }
      };
      // Normalizar el status a minúsculas
      const normalizedStatus = (status || '').toLowerCase();
      config = orderStatusConfig[normalizedStatus] || orderStatusConfig['pending'];
    }

    // Asegurar que config siempre tenga un valor válido
    if (!config) {
      config = { label: status || 'Desconocido', color: '#6b7280', bg: '#f3f4f6' };
    }

    return (
      <span style={{
        padding: '0.375rem 0.75rem',
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        backgroundColor: config.bg,
        color: config.color,
        display: 'inline-block'
      }}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mostrar cargando mientras se verifica la autenticación
  if (authLoading) {
    return <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>Cargando...</div>;
  }

  // Si no hay token, mostrar error de autenticación
  if (!token) {
    return <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>Debes iniciar sesión para ver este pedido</div>;
  }

  if (loading) {
    return <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!order) {
    return <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>Pedido no encontrado</div>;
  }

  return (
    <div className="container" style={{ padding: '2rem 1rem', minHeight: '70vh', maxWidth: '800px' }}>
      <h1 style={{ marginBottom: '2rem' }}>Pedido #{order.orderNumber}</h1>

      <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Estado del Pedido</h3>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '1rem' }}>Estado: </span>
          {getStatusBadge(order.orderStatus || order.status || 'pending', 'order')}
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '1rem' }}>Método de Pago: </span>
          <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>
            {order.paymentMethod === 'efectivo' ? 'Pago en Efectivo' :
             order.paymentMethod === 'transferencia' ? 'Transferencia Bancaria' : order.paymentMethod}
          </span>
        </div>
        <div style={{ fontSize: '0.875rem', color: '#666' }}>
          Fecha del pedido: {formatDate(order.createdAt)}
        </div>
      </div>

      {/* Receipt Section for Transfer Orders */}
      {order.paymentMethod === 'transferencia' && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Comprobante de Transferencia</h3>

          {order.receipt ? (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ marginRight: '1rem' }}>Estado del comprobante: </span>
                {getStatusBadge(order.receipt?.receiptStatus || 'pendiente', 'receipt')}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#666' }}>
                  Subido: {formatDate(order.receipt.uploadedAt)}
                </span>
              </div>

              {order.receipt.receiptPath && (
                <div style={{ marginBottom: '1rem' }}>
                  {order.receipt.receiptMimeType === 'application/pdf' ? (
                    <div style={{ padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                      <p style={{ marginBottom: '0.5rem' }}>📄 Comprobante PDF</p>
                      <a
                        href={`${SERVER_URL}${order.receipt.receiptPath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#2563eb',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '6px'
                        }}
                      >
                        Ver Comprobante
                      </a>
                    </div>
                  ) : (
                    <img
                      src={`${SERVER_URL}${order.receipt.receiptPath}`}
                      alt="Comprobante de transferencia"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                  )}
                </div>
              )}

              {order.receipt.receiptStatus === 'rechazado' && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fee2e2',
                  borderLeft: '4px solid #dc2626',
                  borderRadius: '6px',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#dc2626' }}>Comprobante Rechazado</h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#7f1d1d' }}>
                    {order.receipt.rejectionReason || 'El comprobante no es válido o no se puede verificar.'}
                  </p>
                </div>
              )}

              {(order.receipt.receiptStatus === 'rechazado' || !order.receipt.receiptPath) && !showReceiptUpload && (
                <button
                  onClick={() => setShowReceiptUpload(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {order.receipt.receiptPath ? 'Subir Nuevo Comprobante' : 'Subir Comprobante'}
                </button>
              )}
            </div>
          ) : (
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e' }}>⚠️ Comprobante Requerido</h4>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#92400e' }}>
                Para procesar tu pedido, necesitas subir el comprobante de transferencia bancaria.
              </p>
              {!showReceiptUpload && (
                <button
                  onClick={() => setShowReceiptUpload(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Subir Comprobante
                </button>
              )}
            </div>
          )}

          {showReceiptUpload && (
            <div style={{ marginTop: '1rem' }}>
              <ReceiptUpload
                orderId={order.id}
                onUploadSuccess={handleReceiptUploadSuccess}
                required={true}
              />
              <button
                onClick={() => setShowReceiptUpload(false)}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h3>Productos</h3>
        {order.items.map((item, index) => {
          const hasDiscount = item.discount > 0;
          const originalPrice = item.originalPrice || item.price;
          const finalPrice = item.price;
          const originalTotal = originalPrice * item.quantity;
          const finalTotal = item.total;
          
          return (
          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ flex: 1 }}>
              <p style={{ fontWeight: '500', margin: 0 }}>{item.productName}</p>
              <p style={{ fontSize: '0.9rem', color: '#666', margin: '0.25rem 0' }}>Cantidad: {item.quantity}</p>
              {item.size && <p style={{ fontSize: '0.85rem', color: '#888' }}>Talla: {item.size}</p>}
              {item.color && <p style={{ fontSize: '0.85rem', color: '#888' }}>Color: {item.color}</p>}
                {hasDiscount && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <span style={{ 
                      background: '#ef4444', 
                      color: 'white', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '0.75rem', 
                      fontWeight: '600' 
                    }}>
                      -{item.discount}% Descuento
                    </span>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', minWidth: '150px' }}>
                {hasDiscount ? (
                  <>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.85rem', color: '#666' }}>Precio unitario: </span>
                      <span style={{ color: '#667eea', fontWeight: '600' }}>${finalPrice.toFixed(2)}</span>
                      <span style={{ color: '#999', textDecoration: 'line-through', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                        ${originalPrice.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 'bold', color: '#667eea', fontSize: '1.1rem' }}>
                        ${finalTotal.toFixed(2)}
                      </span>
                      <div style={{ color: '#999', textDecoration: 'line-through', fontSize: '0.85rem' }}>
                        ${originalTotal.toFixed(2)}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                      ${finalPrice.toFixed(2)} c/u
                    </div>
                    <p style={{ fontWeight: 'bold', margin: 0 }}>${finalTotal.toFixed(2)}</p>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <h3>Dirección de Envío</h3>
        <p style={{ margin: '0.5rem 0' }}>{order.shippingAddress.fullName}</p>
        <p style={{ margin: '0.5rem 0', color: '#666' }}>{order.shippingAddress.address}</p>
        <p style={{ margin: '0.5rem 0', color: '#666' }}>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
        <p style={{ margin: '0.5rem 0', color: '#666' }}>Tel: {order.shippingAddress.phone}</p>
      </div>

      <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h3>Resumen</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 'bold' }}>
          <span>Total:</span><span>${order.total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
